"use client";

import { useTranslations } from "next-intl";
import { getServiceFeeDescription, SERVICE_FEE_FIXED, SERVICE_FEE_RATE, SERVICE_FEE_RATE_LEGACY } from "@/lib/config";
import { createLegalRichTags, legalRichValues } from "@/lib/legal/page-rich-tags";
import { ROUTE_GUTTER_CLASS } from "@/lib/route-layout";
import { cn } from "@/lib/utils";

const COMPANY_DETAIL_KEYS = ["taxVatId", "siren", "rcs"] as const;
const S3_AGREE_KEYS = ["accurateInfo", "confidentiality", "compliance"] as const;
const S3_PROHIBITED_KEYS = ["multipleAccounts", "unauthorizedAccount", "fraud"] as const;
const S8_DMCA_KEYS = ["description", "location", "contactDetails", "statement"] as const;
const S10_LIABILITY_KEYS = ["descriptions", "disputes", "unauthorizedAccess", "shipping"] as const;

export default function TermsAndConditions() {
  const t = useTranslations("legal.terms");
  const richTags = createLegalRichTags();
  const richValues = legalRichValues();

  const noFee = [SERVICE_FEE_FIXED, SERVICE_FEE_RATE, SERVICE_FEE_RATE_LEGACY].every((value) => Number(value) === 0);
  const feeStructure = noFee ? t("s5.noServiceFeeApplied") : `${getServiceFeeDescription()}.`;

  return (
    <div className={cn("pt-4 pb-6 flex flex-col justify-center items-center min-h-screen text-left bg-background py-6", ROUTE_GUTTER_CLASS)}>
      <h1 className="pt-6 text-5xl font-bold text-foreground mb-4">{t("title")}</h1>
      <p className="text-lg text-muted-foreground mb-6">{t("effectiveDate")}</p>

      <div className="w-full max-w-4xl text-foreground pb-8">
        <h2 className="text-2xl font-semibold mb-4">{t("s1.title")}</h2>
        <p className="mb-4">{t.rich("s1.websiteIntro", richTags)}</p>
        <ul className="list-disc pl-6 mb-4">
          {COMPANY_DETAIL_KEYS.map((key) => (
            <li key={key}>{t.rich(`s1.companyDetails.${key}`, richTags)}</li>
          ))}
        </ul>
        <p className="mb-4">{t.rich("s1.contactInquiry", { ...richTags, ...richValues })}</p>
        <h3 className="text-xl font-medium mb-2">{t("s1.hostingTitle")}</h3>
        <p className="mb-4">{t.rich("s1.hostingIntro", richTags)}</p>
        <p className="mb-4 whitespace-pre-line">{t("s1.hostingAddress")}</p>
        <p className="mb-4">{t.rich("s1.databaseIntro", richTags)}</p>
        <p className="mb-4 whitespace-pre-line">{t("s1.databaseAddress")}</p>

        <h2 className="text-2xl font-semibold mb-4">{t("s2.title")}</h2>
        <p className="mb-4">{t("s2.ageRequirement")}</p>
        <p className="mb-4">{t("s2.accountResponsibility")}</p>

        <h2 className="text-2xl font-semibold mb-4">{t("s3.title")}</h2>
        <p className="mb-4">{t("s3.intro")}</p>
        <ul className="list-disc pl-6 mb-4">
          {S3_AGREE_KEYS.map((key) => (
            <li key={key}>{t.rich(`s3.agreeBullets.${key}`, richTags)}</li>
          ))}
        </ul>
        <p className="mb-4">{t("s3.prohibitedIntro")}</p>
        <ul className="list-disc pl-6 mb-4">
          {S3_PROHIBITED_KEYS.map((key) => (
            <li key={key}>{t(`s3.prohibitedBullets.${key}`)}</li>
          ))}
        </ul>

        <h2 className="text-2xl font-semibold mb-4">{t("s4.title")}</h2>
        <p className="mb-4">{t("s4.sellerResponsibility")}</p>
        <p className="mb-4">{t.rich("s4.postsPolicy", richTags)}</p>
        <p className="mb-4">{t("s4.buyerSellerDisputes")}</p>

        <h2 className="text-2xl font-semibold mb-4">{t("s5.title")}</h2>
        <p className="mb-4">{t.rich("s5.noSellerFees", richTags)}</p>
        <p className="mb-4">{t.rich("s5.buyerServiceFee", { ...richTags, structure: feeStructure })}</p>
        <p className="mb-4">{t.rich("s5.nonRefundable", richTags)}</p>
        <p className="mb-4">{t.rich("s5.adjustments", richTags)}</p>
        <p className="mb-4 text-sm text-muted-foreground">{t("s5.externalProcessorsNote")}</p>

        <h2 className="text-2xl font-semibold mb-4">{t("s6.title")}</h2>
        <p className="mb-4">{t.rich("s6.processing", richTags)}</p>
        <p className="mb-4">{t.rich("s6.taxesAndFees", richTags)}</p>
        <p className="mb-4">{t.rich("s6.disputes", richTags)}</p>

        <h2 className="text-2xl font-semibold mb-4">{t("s7.title")}</h2>
        <p className="mb-4">{t.rich("s7.guidelinesReference", richTags)}</p>
        <p className="mb-4">{t("s7.monitoring")}</p>

        <h2 className="text-2xl font-semibold mb-4">{t("s8.title")}</h2>
        <h3 className="text-xl font-medium mb-2">{t("s8.ownershipTitle")}</h3>
        <p className="mb-4">{t("s8.ownershipBody")}</p>
        <h3 className="text-xl font-medium mb-2">{t("s8.userContentTitle")}</h3>
        <p className="mb-4">{t("s8.userContentBody")}</p>
        <h3 className="text-xl font-medium mb-2">{t("s8.dmcaTitle")}</h3>
        <p className="mb-4">{t.rich("s8.dmcaIntro", { ...richTags, ...richValues })}</p>
        <ul className="list-disc pl-6 mb-4">
          {S8_DMCA_KEYS.map((key) => (
            <li key={key}>{t(`s8.dmcaBullets.${key}`)}</li>
          ))}
        </ul>
        <p className="mb-4">{t("s8.dmcaFollowUp")}</p>

        <h2 className="text-2xl font-semibold mb-4">{t("s9.title")}</h2>
        <p className="mb-4">{t("s9.sellerShipping")}</p>
        <p className="mb-4">{t("s9.noLiability")}</p>
        <p className="mb-4">{t("s9.disputeResolution")}</p>

        <h2 className="text-2xl font-semibold mb-4">{t("s10.title")}</h2>
        <p className="mb-4">{t("s10.intro")}</p>
        <ul className="list-disc pl-6 mb-4">
          {S10_LIABILITY_KEYS.map((key) => (
            <li key={key}>{t(`s10.bullets.${key}`)}</li>
          ))}
        </ul>

        <h2 className="text-2xl font-semibold mb-4">{t("s11.title")}</h2>
        <p className="mb-4">{t.rich("s11.body", richTags)}</p>

        <h2 className="text-2xl font-semibold mb-4">{t("s12.title")}</h2>
        <p className="mb-4">{t("s12.body")}</p>

        <h2 className="text-2xl font-semibold mb-4">{t("s13.title")}</h2>
        <p className="mb-4">{t("s13.body")}</p>

        <p className="mb-4">{t.rich("footer", { ...richTags, ...richValues })}</p>
      </div>
    </div>
  );
}
